import { useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const PAYPAL_CLIENT_ID = "AazO-rgM0P92M0hCAc4siJcohf9fZkd8J20dcyrjdSYlDKO-bd-weMEWcAekLde70ZDGa1Bo2_fN2Tfd";

const PLAN_IDS = {
  basic: "P-2L7246653F226472XNFCUJRA",
  premium: "P-7ES49391R1928661LNFCUKGQ",
} as const;

declare global {
  interface Window {
    paypal?: any;
  }
}

export function usePayPalPayment() {
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [isScriptLoading, setIsScriptLoading] = useState(false);
  const [isRevising, setIsRevising] = useState(false);
  const scriptLoadedRef = useRef(false);
  const { toast } = useToast();

  const loadScript = useCallback(() => {
    return new Promise<void>((resolve, reject) => {
      if (window.paypal) {
        setIsScriptLoaded(true);
        resolve();
        return;
      }

      if (scriptLoadedRef.current || isScriptLoading) {
        const checkScript = setInterval(() => {
          if (window.paypal) {
            clearInterval(checkScript);
            setIsScriptLoaded(true);
            resolve();
          }
        }, 100);
        return;
      }

      setIsScriptLoading(true);
      scriptLoadedRef.current = true;

      const script = document.createElement("script");
      script.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&vault=true&intent=subscription`;
      script.setAttribute("data-sdk-integration-source", "button-factory");
      
      script.onload = () => {
        setIsScriptLoaded(true);
        setIsScriptLoading(false);
        resolve();
      };
      
      script.onerror = () => {
        setIsScriptLoading(false);
        scriptLoadedRef.current = false;
        reject(new Error("Failed to load PayPal SDK"));
      };

      document.body.appendChild(script);
    });
  }, [isScriptLoading]);

  // Revise existing subscription to a new plan (no re-entering payment details)
  const reviseSubscription = useCallback(async (
    currentSubscriptionId: string,
    newPlan: "basic" | "premium",
    returnUrl?: string
  ): Promise<{ success: boolean; immediate?: boolean; is_downgrade?: boolean; approval_url?: string }> => {
    setIsRevising(true);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: "Error",
          description: "Please log in to change your plan.",
          variant: "destructive",
        });
        return { success: false };
      }

      const baseUrl = window.location.origin;
      const callbackUrl = returnUrl || `${baseUrl}/subscription/callback`;

      const { data, error } = await supabase.functions.invoke("revise-paypal-subscription", {
        body: {
          subscription_id: currentSubscriptionId,
          new_plan: newPlan,
          return_url: callbackUrl,
        },
      });

      if (error) {
        console.error("Error revising subscription:", error);
        toast({
          title: "Error",
          description: "Failed to initiate plan change. Please try again.",
          variant: "destructive",
        });
        return { success: false };
      }

      const isDowngrade = data.is_downgrade === true;

      if (data.immediate) {
        // Plan change was applied immediately (no PayPal approval needed)
        // Show appropriate toast based on upgrade vs downgrade
        if (isDowngrade) {
          toast({
            title: "Downgrade Scheduled",
            description: "Your plan will change to Basic at the end of your current billing cycle.",
          });
        } else {
          toast({
            title: "Processing Upgrade",
            description: "Your upgrade is being processed. This may take a moment...",
          });
        }
        return { success: true, immediate: true, is_downgrade: isDowngrade };
      }

      if (data.approval_url) {
        // Redirect user to PayPal to approve the change
        return { success: true, approval_url: data.approval_url, is_downgrade: isDowngrade };
      }

      return { success: false };
    } catch (error) {
      console.error("Error in reviseSubscription:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
      return { success: false };
    } finally {
      setIsRevising(false);
    }
  }, [toast]);

  const renderButton = useCallback(async (
    containerId: string,
    plan: "basic" | "premium",
    onSuccess?: () => void
  ) => {
    try {
      await loadScript();

      if (!window.paypal) {
        throw new Error("PayPal SDK not available");
      }

      const container = document.getElementById(containerId);
      if (!container) {
        console.error("Container not found:", containerId);
        return;
      }

      container.innerHTML = "";

      const { data: { user } } = await supabase.auth.getUser();
      const userId = user?.id;

      window.paypal.Buttons({
        style: {
          shape: "rect",
          color: "gold",
          layout: "vertical",
          label: "subscribe",
        },
        createSubscription: function(data: any, actions: any) {
          return actions.subscription.create({
            plan_id: PLAN_IDS[plan],
            custom_id: userId,
          });
        },
        onApprove: async function(data: any) {
          console.log("PayPal subscription approved:", data.subscriptionID);
          
          // Don't update database here - let the webhook handle it as single source of truth
          // This prevents race conditions and ensures consistency
          
          toast({
            title: "Subscription Processing",
            description: "Your subscription is being activated. This may take a moment.",
          });

          onSuccess?.();
        },
        onError: function(err: any) {
          console.error("PayPal error:", err);
          toast({
            title: "Payment Failed",
            description: "There was an error processing your payment. Please try again.",
            variant: "destructive",
          });
        },
        onCancel: function() {
          toast({
            title: "Payment Cancelled",
            description: "You cancelled the payment process.",
          });
        },
      }).render(`#${containerId}`);
    } catch (error) {
      console.error("Error rendering PayPal button:", error);
      toast({
        title: "Error",
        description: "Failed to load payment options. Please refresh the page.",
        variant: "destructive",
      });
    }
  }, [loadScript, toast]);

  return {
    isScriptLoaded,
    isScriptLoading,
    isRevising,
    loadScript,
    renderButton,
    reviseSubscription,
    planIds: PLAN_IDS,
  };
}
